import { useEffect, useState } from 'react';

import { ApiError } from '@eneco/dc/client/ApiError';
import { getWarmthProfile } from '@eneco/dc/services';
import { typedYupResolver } from '@eneco/hooks/form';
import {
  DC_Repositories_Base_Enumerations_BusinessUnit,
  DC_Repositories_Base_Enumerations_Label,
} from '@monorepo-types/dc';
import { WarmthCheckAddressFinderRendering } from '@sitecore/types/WarmthCheckAddressFinder';
import { useRouter } from 'next/router';
import { useForm, useWatch } from 'react-hook-form';

import { mapErrorToMessage } from '../utils/errorMapping';
import { createFormSchema } from '../utils/formSchema';
import { navigateToProfileGroup } from '../utils/navigation';
import { WarmCheckerFormFields } from '../utils/types';

interface ErrorState {
  title: string;
  content: string;
}

const useWarmChecker = (textualData: WarmthCheckAddressFinderRendering['fields']) => {
  const router = useRouter();
  const [isHouseNumberSuffixRequired, setIsHouseNumberSuffixRequired] = useState(false);
  const [error, setError] = useState<ErrorState | null>(null);

  const formSchema = createFormSchema(isHouseNumberSuffixRequired);

  const formMethods = useForm<WarmCheckerFormFields>({
    mode: 'onBlur',
    resolver: typedYupResolver<WarmCheckerFormFields>(formSchema),
  });

  const { control, clearErrors } = formMethods;
  const { postalCode, houseNumber, houseNumberSuffix } = useWatch({ control });

  useEffect(() => {
    if (postalCode || houseNumber || houseNumberSuffix) {
      setError(null);
    }
  }, [postalCode, houseNumber, houseNumberSuffix]);

  const handleFormSubmit = async (formData: WarmCheckerFormFields) => {
    try {
      clearErrors();
      setError(null);

      const response = await getWarmthProfile({
        label: 'eneco' as DC_Repositories_Base_Enumerations_Label,
        businessUnit: 'nl' as DC_Repositories_Base_Enumerations_BusinessUnit,
        postalCode: formData.postalCode,
        houseNumber: Number.parseInt(formData.houseNumber),
        houseNumberSuffix: formData.houseNumberSuffix,
      });

      if (response?.data?.profileGroup) {
        navigateToProfileGroup(router, response.data.profileGroup, textualData.profileGroupProductPageRedirects);
      }
    } catch (err) {
      const status = (err as ApiError).status;
      const mappedError = mapErrorToMessage(status, textualData);
      setError(mappedError || null);
    }
  };

  return {
    formMethods,
    error,
    handleFormSubmit,
    isHouseNumberSuffixRequired,
    setIsHouseNumberSuffixRequired,
  };
};

export default useWarmChecker;
